import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Inline SVG icon helper (Feather-style outline)
const Icon = ({ paths, size = 16, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
       stroke={color || 'currentColor'} strokeWidth="1.5"
       strokeLinecap="round" strokeLinejoin="round">
    {paths.map((d, i) => <path key={i} d={d} />)}
  </svg>
);
const IconMail   = ({ color }) => <Icon color={color} paths={["M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z","m22 6-10 7L2 6"]}/>;
const IconShield = ({ color }) => <Icon color={color} paths={["M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z","M9 12l2 2 4-4"]}/>;
const IconCheck  = ({ color }) => <Icon color={color} paths={["M9 11 12 14 22 4","M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"]}/>;
const IconFolder = ({ color }) => <Icon color={color} paths={["M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"]}/>;

// Format a date for the "from" line of unread email rows
function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d`;
}

// Format a Google Calendar event date as "FRI · MAY 22"
function formatEventDate(iso, allDay) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const weekday = d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  const monthDay = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();
  return `${weekday} · ${monthDay}`;
}

function formatEventTime(start, end, allDay) {
  if (allDay) return 'All day';
  if (!start) return '';
  const s = new Date(start);
  if (isNaN(s.getTime())) return '';
  const opts = { hour: 'numeric', minute: '2-digit' };
  const startStr = s.toLocaleTimeString('en-US', opts);
  if (!end) return startStr;
  const e = new Date(end);
  if (isNaN(e.getTime())) return startStr;
  return `${startStr} – ${e.toLocaleTimeString('en-US', opts)}`;
}

// Placeholder data — to be replaced when integrations exist
const ASANA_TASKS = [
  { title: 'Update vendor list', due: 'Today', isToday: true },
  { title: 'Programs budget review', due: 'Fri' },
  { title: 'Onboard new intern', due: 'May 27' }
];

// KnowBe4 risk score is 0–100 where HIGHER = MORE risk (0 is best).
const riskColor = (s) => (typeof s !== 'number') ? '#6B7280' : s >= 70 ? '#993C1D' : s >= 30 ? '#B45309' : '#639922';

const EXHIBITS = [
  { name: 'Rahim Fortune: Between a Memory and Me', dates: 'Apr 18 – Sep 5, 2026', image: '/exhibits/rahim-fortune.png' },
  { name: 'Kinship & Community', dates: 'Mar 24 – Sep 13, 2026', image: '/exhibits/kinship-community.png' },
  { name: 'The Sound of Gordon Parks', dates: 'Apr 18 – Sep 5, 2026', image: '/exhibits/gordon-parks.png' },
  { name: 'A New Song: Langston Hughes in the West', dates: 'Apr 7 – Sep 13, 2026', image: '/exhibits/langston-hughes.png' }
];

export default function Dashboard({ user, navigateTo }) {
  const [quote, setQuote] = useState(null);
  const [emails, setEmails] = useState(null);  // { count, recent: [{from, time}] }
  const [files, setFiles] = useState(null);    // [{name, modifiedTime}]
  const [events, setEvents] = useState(null);  // [{title, start, location, ...}]
  const [calendarError, setCalendarError] = useState(null);
  const [kb4, setKb4] = useState(null);        // { configured, matched, risk_score, phish_prone_percentage, training, last_phishing }
  const [kb4Team, setKb4Team] = useState(null);// admin only: { summary, users }

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  // Fetch the daily quote (no auth needed)
  useEffect(() => {
    fetch(`${API_URL}/quote/daily`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setQuote(d))
      .catch(() => setQuote(null));
  }, []);

  // Fetch unread email summary
  useEffect(() => {
    fetch(`${API_URL}/google/messages?folder=INBOX&q=is:unread`, { headers: authHeader() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data || !data.messages) return setEmails({ count: 0, recent: [] });
        const recent = data.messages.slice(0, 3).map(m => {
          const fromHeader = (m.from || '').replace(/<.*>/, '').replace(/"/g, '').trim();
          return { from: fromHeader || '(no sender)', time: timeAgo(m.date) };
        });
        setEmails({ count: data.messages.length, recent });
      })
      .catch(() => setEmails({ count: 0, recent: [] }));
  }, []);

  // Fetch recent Drive files
  useEffect(() => {
    fetch(`${API_URL}/google/drive/files?folderId=root`, { headers: authHeader() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data || !data.files) return setFiles([]);
        const sorted = [...data.files]
          .filter(f => f.mimeType !== 'application/vnd.google-apps.folder')
          .sort((a, b) => new Date(b.modifiedTime) - new Date(a.modifiedTime))
          .slice(0, 3);
        setFiles(sorted);
      })
      .catch(() => setFiles([]));
  }, []);

  // Fetch this user's own KnowBe4 security metrics
  useEffect(() => {
    fetch(`${API_URL}/knowbe4/me`, { headers: authHeader() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setKb4(d || { configured: false }))
      .catch(() => setKb4({ configured: false }));
  }, []);

  // Admins also get the org-wide KnowBe4 summary
  useEffect(() => {
    if (user?.role !== 'admin') return;
    fetch(`${API_URL}/knowbe4/users`, { headers: authHeader() })
      .then(r => r.ok ? r.json() : null)
      .then(d => d && d.configured && setKb4Team(d))
      .catch(() => {});
  }, [user]);

  // Fetch Google Calendar events
  useEffect(() => {
    fetch(`${API_URL}/google/calendar/events`, { headers: authHeader() })
      .then(async r => {
        // 401 = Google not connected; 403 = scope not granted. Both mean "user needs to (re)connect Google".
        if (r.status === 401 || r.status === 403) {
          setCalendarError('reconnect');
          return null;
        }
        if (!r.ok) {
          setCalendarError('error');
          return null;
        }
        return r.json();
      })
      .then(data => {
        if (data && data.events) setEvents(data.events.slice(0, 3));
      })
      .catch(() => setCalendarError('error'));
  }, []);

  const firstName = (user?.first_name || user?.name || 'there').split(' ')[0];

  return (
    <div style={styles.page}>
      {/* Welcome + quote */}
      <div style={styles.welcomeRow}>
        <span style={styles.emoji}>👋</span>
        <h1 style={styles.welcomeText}>Welcome back, {firstName}</h1>
      </div>
      {quote && (
        <div style={styles.quote}>
          <span>"{quote.text}"</span>
          <span style={styles.quoteAttrib}> — {quote.author}</span>
        </div>
      )}

      {/* Metric cards */}
      <div style={styles.metricGrid}>
        {/* Unread Email */}
        <div style={styles.card}>
          <div style={styles.cardHead}>
            <span style={styles.cardLabel}>Unread email</span>
            <IconMail color="var(--caam-accent)"/>
          </div>
          {emails === null ? (
            <div style={styles.cardLoading}>—</div>
          ) : (
            <>
              <div style={styles.cardBigNum}>{emails.count}</div>
              <div style={styles.cardDetail}>
                {emails.recent.length === 0 ? (
                  <div style={styles.muted}>Inbox zero</div>
                ) : emails.recent.map((m, i) => (
                  <div key={i} style={styles.detailRow}>
                    <span style={styles.detailMain}>{m.from}</span>
                    <span style={styles.detailSub}>{m.time}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* KnowBe4 — the logged-in user's own security metrics (admins also see a team summary) */}
        <div style={styles.card}>
          <div style={styles.cardHead}>
            <span style={styles.cardLabel}>KnowBe4 security</span>
            <IconShield color={riskColor(kb4?.risk_score)}/>
          </div>

          {!kb4 ? (
            <div style={styles.cardBigNum}><span style={{...styles.cardBigSuffix, fontSize: '14px'}}>Loading…</span></div>
          ) : kb4.configured === false ? (
            <>
              <div style={styles.cardBigNum}><span style={{...styles.cardBigSuffix, fontSize: '14px'}}>Not connected</span></div>
              <div style={styles.cardDetail}>
                <div style={styles.detailRow}><span style={styles.detailSub}>KnowBe4 API token not configured</span></div>
              </div>
            </>
          ) : kb4.matched === false ? (
            <>
              <div style={styles.cardBigNum}><span style={{...styles.cardBigSuffix, fontSize: '14px'}}>No profile</span></div>
              <div style={styles.cardDetail}>
                <div style={styles.detailRow}><span style={styles.detailSub}>No matching KnowBe4 user for your email</span></div>
              </div>
            </>
          ) : (
            <>
              <div style={{...styles.cardBigNum, color: riskColor(kb4.risk_score)}}>
                {typeof kb4.risk_score === 'number' ? Math.round(kb4.risk_score) : '—'}
                <span style={styles.cardBigSuffix}> risk</span>
              </div>
              <div style={styles.cardDetail}>
                <div style={styles.detailRow}>
                  <span style={styles.detailMain}>Phish-prone</span>
                  <span style={styles.detailSub}>{typeof kb4.phish_prone_percentage === 'number' ? `${kb4.phish_prone_percentage}%` : '—'}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailMain}>Training</span>
                  <span style={(kb4.training?.overdue > 0) ? {...styles.detailSub, color: '#993C1D'} : styles.detailSub}>
                    {kb4.training?.overdue > 0
                      ? `${kb4.training.overdue} overdue`
                      : `${kb4.training?.passed ?? 0}/${kb4.training?.total ?? 0} done`}
                  </span>
                </div>
                {kb4.last_phishing && (
                  <div style={styles.detailRow}>
                    <span style={styles.detailMain}>Last phish</span>
                    <span style={kb4.last_phishing.clicked ? {...styles.detailSub, color: '#993C1D'} : styles.detailSub}>
                      {kb4.last_phishing.status}
                    </span>
                  </div>
                )}
                {kb4Team?.summary && (
                  <div style={styles.detailRow}>
                    <span style={styles.detailMain}>Team avg risk</span>
                    <span style={(kb4Team.summary.high_risk > 0) ? {...styles.detailSub, color: '#993C1D'} : styles.detailSub}>
                      {kb4Team.summary.avg_risk ?? '—'}{kb4Team.summary.high_risk > 0 ? ` · ${kb4Team.summary.high_risk} high` : ''}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Asana */}
        <div style={styles.card}>
          <div style={styles.cardHead}>
            <span style={styles.cardLabel}>Asana tasks</span>
            <IconCheck color="#185FA5"/>
          </div>
          <div style={styles.cardBigNum}>{ASANA_TASKS.length}</div>
          <div style={styles.cardDetail}>
            {ASANA_TASKS.map((t, i) => (
              <div key={i} style={styles.detailRow}>
                <span style={styles.detailMain}>{t.title}</span>
                <span style={t.isToday ? {...styles.detailSub, color: '#993C1D'} : styles.detailSub}>{t.due}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Files */}
        <div style={styles.card}>
          <div style={styles.cardHead}>
            <span style={styles.cardLabel}>Recent files</span>
            <IconFolder color="var(--caam-text-muted)"/>
          </div>
          {files === null ? (
            <div style={styles.cardLoading}>—</div>
          ) : files.length === 0 ? (
            <div style={{...styles.cardDetail, ...styles.muted}}>No files</div>
          ) : (
            <div style={{...styles.cardDetail, marginTop: 4}}>
              {files.map((f, i) => (
                <div key={i} style={{...styles.detailRow, alignItems: 'baseline'}}>
                  <span style={styles.detailMain} title={f.name}>{f.name}</span>
                </div>
              ))}
              <div style={{...styles.detailSub, marginTop: 6}}>Last opened recently</div>
            </div>
          )}
        </div>
      </div>

      {/* Upcoming events */}
      <div style={styles.sectionLabel}>
        <span>Upcoming events</span>
        {/* future link placeholder */}
      </div>
      {calendarError === 'reconnect' ? (
        <div style={styles.calendarNotice}>
          Reconnect Google to view upcoming events from the CAAM calendar.
        </div>
      ) : events === null ? (
        <div style={styles.eventsLoading}>Loading events…</div>
      ) : events.length === 0 ? (
        <div style={styles.eventsEmpty}>No events scheduled in the next 14 days.</div>
      ) : (
        <div style={styles.eventsGrid}>
          {events.map((ev, i) => (
            <a key={ev.id || i} href={ev.htmlLink || '#'}
               target="_blank" rel="noopener noreferrer"
               style={styles.eventCard}>
              <div style={styles.eventDate}>{formatEventDate(ev.start, ev.allDay)}</div>
              <div style={styles.eventTitle}>{ev.title}</div>
              <div style={styles.eventMeta}>
                {formatEventTime(ev.start, ev.end, ev.allDay)}
                {ev.location ? ` · ${ev.location}` : ''}
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Current exhibits */}
      <div style={styles.sectionLabel}>
        <span>Current exhibits</span>
      </div>
      <div style={styles.exhibitsGrid}>
        {EXHIBITS.map((ex, i) => (
          <div key={i} style={styles.exhibitCard}>
            <div style={{...styles.exhibitImage, backgroundImage: `url(${ex.image})`}} />
            <div style={styles.exhibitBody}>
              <div style={styles.exhibitTitle}>{ex.name}</div>
              <div style={styles.exhibitMeta}>{ex.dates}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: '32px 40px',
    maxWidth: 1400,
    margin: '0 auto'
  },
  welcomeRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 12,
    marginBottom: 4
  },
  emoji: { fontSize: 30 },
  welcomeText: {
    fontSize: 26,
    fontWeight: 600,
    color: 'var(--caam-text)',
    margin: 0,
    letterSpacing: '-0.01em'
  },
  quote: {
    fontStyle: 'italic',
    color: 'var(--caam-text-muted)',
    fontSize: 13,
    borderLeft: '2px solid var(--caam-accent)',
    padding: '6px 14px',
    margin: '18px 0 24px',
    fontFamily: 'Georgia, "Times New Roman", serif'
  },
  quoteAttrib: {
    fontStyle: 'normal',
    color: 'var(--caam-text-light)'
  },
  metricGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 14,
    marginBottom: 28
  },
  card: {
    background: '#fff',
    border: '0.5px solid var(--caam-border)',
    borderRadius: 10,
    padding: 16,
    minHeight: 160,
    display: 'flex',
    flexDirection: 'column'
  },
  cardHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12
  },
  cardLabel: {
    fontSize: 11,
    color: 'var(--caam-text-muted)',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    fontWeight: 600
  },
  cardBigNum: {
    fontSize: 30,
    fontWeight: 600,
    color: 'var(--caam-text)',
    lineHeight: 1,
    marginBottom: 10
  },
  cardBigSuffix: {
    fontSize: 14,
    color: 'var(--caam-text-muted)',
    fontWeight: 400
  },
  cardLoading: { color: 'var(--caam-text-light)', fontSize: 13 },
  cardDetail: { fontSize: 12, color: 'var(--caam-text)', lineHeight: 1.7 },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    alignItems: 'center'
  },
  detailMain: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  detailSub: {
    color: 'var(--caam-text-muted)',
    fontSize: 11,
    flexShrink: 0
  },
  muted: { color: 'var(--caam-text-muted)' },
  sectionLabel: {
    fontSize: 12,
    color: 'var(--caam-text-muted)',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    marginBottom: 10,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  eventsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 14,
    marginBottom: 28
  },
  eventCard: {
    background: '#fff',
    border: '0.5px solid var(--caam-border)',
    borderRadius: 10,
    padding: 14,
    textDecoration: 'none',
    color: 'inherit',
    display: 'block'
  },
  eventDate: {
    fontSize: 11,
    color: 'var(--caam-accent)',
    fontWeight: 600,
    marginBottom: 4,
    letterSpacing: '0.04em'
  },
  eventTitle: {
    fontSize: 14,
    color: 'var(--caam-text)',
    fontWeight: 500,
    marginBottom: 4
  },
  eventMeta: {
    fontSize: 11,
    color: 'var(--caam-text-muted)'
  },
  eventsLoading: {
    padding: '14px 0',
    color: 'var(--caam-text-muted)',
    fontSize: 13,
    marginBottom: 28
  },
  eventsEmpty: {
    padding: 14,
    background: '#fff',
    border: '0.5px solid var(--caam-border)',
    borderRadius: 10,
    color: 'var(--caam-text-muted)',
    fontSize: 13,
    marginBottom: 28
  },
  calendarNotice: {
    padding: 14,
    background: 'var(--caam-cream-deep)',
    border: '0.5px solid var(--caam-border)',
    borderRadius: 10,
    color: 'var(--caam-text)',
    fontSize: 13,
    marginBottom: 28
  },
  exhibitsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 14,
    marginBottom: 24
  },
  exhibitCard: {
    background: '#fff',
    border: '0.5px solid var(--caam-border)',
    borderRadius: 10,
    overflow: 'hidden'
  },
  exhibitImage: {
    height: 200,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  },
  exhibitBody: { padding: '14px 16px' },
  exhibitTitle: {
    fontSize: 15,
    color: 'var(--caam-text)',
    fontWeight: 500,
    marginBottom: 4
  },
  exhibitMeta: {
    fontSize: 11,
    color: 'var(--caam-text-muted)'
  }
};
